import {
  createContext,
  useState,
  ReactNode,
  ReactElement,
  useEffect,
} from 'react'

import { User, UserContextType } from '@types'

const UserContext = createContext<UserContextType | undefined>(undefined)

interface UserProviderProps {
  children: ReactNode
}

const UserProvider = ({ children }: UserProviderProps): ReactElement => {
  const [user, setUser] = useState<User | null>(null)

  // Enhanced setUser that also updates sessionStorage
  const updateUser = (newUser: User | null): void => {
    setUser(newUser)

    if (newUser) {
      try {
        window.sessionStorage.setItem('user', JSON.stringify(newUser))
      } catch (error) {
        console.error('Failed to save user to session storage:', error)
      }
    } else {
      // Clear all user-related session data on logout/clear
      window.sessionStorage.removeItem('user')
      window.sessionStorage.removeItem('formValues')
    }
  }

  const restoreUser = (): void => {
    try {
      const storedUser = window.sessionStorage.getItem('user')
      if (storedUser && !user) {
        const parsedUser: User = JSON.parse(storedUser)
        if (parsedUser.id) {
          setUser(parsedUser)
        }
      }
    } catch (error) {
      console.error('Failed to restore user from session:', error)
      window.sessionStorage.removeItem('user')
    }
  }

  useEffect(() => {
    restoreUser()
  }, [user])

  return (
    <UserContext.Provider value={{ user, setUser: updateUser }}>
      {children}
    </UserContext.Provider>
  )
}

export { UserContext, UserProvider }
